import React, { useState } from 'react';
import { FileText, Download, Eye, X, ChevronRight } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import useInView from '../components/useInView';
import CTABanner from '../components/CTABanner';

const DocumentPreview = ({ task, onClose }) => (
  <div style={{
    position: 'fixed',
    inset: 0,
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: 'rgba(0,0,0,0.85)',
    backdropFilter: 'blur(8px)',
    animation: 'fadeInUp 0.3s ease',
  }} onClick={onClose}>
    <div
      style={{
        background: 'rgba(18,22,36,0.98)',
        backdropFilter: 'blur(20px)',
        border: `1px solid ${task.color}40`,
        borderRadius: '24px',
        padding: '0',
        maxWidth: '720px',
        width: '100%',
        maxHeight: '85vh',
        overflow: 'auto',
        boxShadow: `0 40px 120px ${task.color}30`,
        animation: 'scale-in 0.3s ease',
      }}
      onClick={e => e.stopPropagation()}
    >
      {/* Header */}
      <div style={{
        padding: '28px 32px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        background: 'rgba(18,22,36,0.98)',
        zIndex: 1,
      }}>
        <div>
          <div style={{
            display: 'inline-flex',
            padding: '4px 12px',
            borderRadius: '6px',
            background: `${task.color}18`,
            fontSize: '11px',
            fontWeight: 700,
            color: task.color,
            marginBottom: '8px',
          }}>
            ПРИМЕР ДОКУМЕНТА
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#fff' }}>{task.title}</h3>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: 'none',
            borderRadius: '10px',
            padding: '10px',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.6)',
          }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Document content */}
      <div style={{ padding: '28px 32px' }}>
        {task.docContent}
      </div>
    </div>
  </div>
);

// Document content templates
const PassportDoc = () => (
  <div style={{ fontFamily: 'monospace', fontSize: '12px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.8 }}>
    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
      <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff', marginBottom: '4px' }}>
        ПАСПОРТ ПРОЕКТА
      </div>
      <div style={{ color: 'rgba(255,255,255,0.4)' }}>в соответствии с ГОСТ Р 54869-2011</div>
    </div>
    {[
      ['Наименование проекта', 'Разработка мобильного приложения для учёта проектов'],
      ['Основание для открытия', 'Приказ ректора № 142 от 01.09.2024'],
      ['Руководитель проекта', 'Иванов И.И., гр. 1234'],
      ['Куратор проекта', 'проф. Петров А.В.'],
      ['Цель проекта', 'Создать прототип мобильного приложения для управления учебными проектами'],
      ['Дата начала', '01.09.2024'],
      ['Дата завершения', '31.12.2024'],
      ['Бюджет проекта', '50 000 руб.'],
      ['Статус', 'Активный'],
    ].map(([key, val], i) => (
      <div key={i} style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1.5fr',
        gap: '12px',
        padding: '10px 0',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
      }}>
        <span style={{ color: 'rgba(255,255,255,0.4)' }}>{key}:</span>
        <span style={{ color: '#fff' }}>{val}</span>
      </div>
    ))}
    <div style={{
      marginTop: '20px',
      padding: '16px',
      background: 'rgba(46,90,255,0.08)',
      borderRadius: '10px',
    }}>
      <div style={{ color: 'rgba(255,255,255,0.4)', marginBottom: '8px' }}>Ключевые заинтересованные стороны:</div>
      {['Студенческое самоуправление', 'Кафедра менеджмента', 'ИТ-отдел ГУАП', 'Конечные пользователи — студенты'].map((s, i) => (
        <div key={i} style={{ color: 'rgba(255,255,255,0.7)', paddingLeft: '12px' }}>• {s}</div>
      ))}
    </div>
  </div>
);

const WBSDoc = () => (
  <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.8 }}>
    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
      <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff', marginBottom: '4px' }}>
        ИЕРАРХИЧЕСКАЯ СТРУКТУРА РАБОТ (ИСР)
      </div>
      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>п. 5.3.1 ГОСТ Р 54869-2011</div>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {[
        { level: 0, num: '1', text: 'Проект: Мобильное приложение', color: '#2E5AFF' },
        { level: 1, num: '1.1', text: 'Инициация', color: '#5B8AFF' },
        { level: 2, num: '1.1.1', text: 'Разработка паспорта проекта' },
        { level: 2, num: '1.1.2', text: 'Анализ стейкхолдеров' },
        { level: 1, num: '1.2', text: 'Планирование', color: '#8A2BE2' },
        { level: 2, num: '1.2.1', text: 'Разработка ИСР' },
        { level: 2, num: '1.2.2', text: 'Составление календарного плана' },
        { level: 2, num: '1.2.3', text: 'Разработка бюджета' },
        { level: 1, num: '1.3', text: 'Реализация', color: '#FF3B9A' },
        { level: 2, num: '1.3.1', text: 'Проектирование интерфейса' },
        { level: 2, num: '1.3.2', text: 'Разработка прототипа' },
        { level: 2, num: '1.3.3', text: 'Тестирование' },
        { level: 1, num: '1.4', text: 'Завершение', color: '#C050FF' },
        { level: 2, num: '1.4.1', text: 'Сдача результатов' },
        { level: 2, num: '1.4.2', text: 'Архивирование документов' },
      ].map((item, i) => (
        <div
          key={i}
          style={{
            paddingLeft: item.level > 0 ? '16px' : '0',
            paddingTop: '6px',
            paddingBottom: '6px',
            borderLeft: item.level > 0 ? '1px solid rgba(255,255,255,0.06)' : 'none',
            marginLeft: item.level > 0 ? `${(item.level - 1) * 24 + 8}px` : '0',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <span style={{
            fontFamily: 'monospace',
            fontSize: '11px',
            color: item.color || 'rgba(255,255,255,0.3)',
            fontWeight: 700,
            minWidth: '40px',
          }}>
            {item.num}
          </span>
          <span style={{ color: item.level === 0 ? '#fff' : item.level === 1 ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.6)' }}>
            {item.text}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const RiskDoc = () => (
  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)' }}>
    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
      <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff', marginBottom: '4px' }}>РЕЕСТР РИСКОВ</div>
      <div style={{ color: 'rgba(255,255,255,0.4)' }}>п. 5.3.6 ГОСТ Р 54869-2011</div>
    </div>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr>
            {['№', 'Риск', 'Вероятность', 'Влияние', 'Ранг', 'Меры'].map(h => (
              <th key={h} style={{
                padding: '10px 8px',
                textAlign: 'left',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                color: 'rgba(255,255,255,0.4)',
                fontWeight: 600,
                fontSize: '11px',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[
            { n: 1, risk: 'Нехватка времени', prob: 'Высокая', imp: 'Высокое', rank: '9', color: '#FF3B9A', action: 'Пересмотр расписания' },
            { n: 2, risk: 'Изменение требований', prob: 'Средняя', imp: 'Высокое', rank: '6', color: '#FF8C00', action: 'Управление изменениями' },
            { n: 3, risk: 'Недостаток бюджета', prob: 'Низкая', imp: 'Среднее', rank: '3', color: '#2E5AFF', action: 'Резервный фонд' },
            { n: 4, risk: 'Болезнь участника', prob: 'Средняя', imp: 'Среднее', rank: '4', color: '#2E5AFF', action: 'Дублирование ролей' },
          ].map(row => (
            <tr key={row.n} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <td style={{ padding: '10px 8px', color: 'rgba(255,255,255,0.4)' }}>{row.n}</td>
              <td style={{ padding: '10px 8px', color: '#fff' }}>{row.risk}</td>
              <td style={{ padding: '10px 8px', color: row.color }}>{row.prob}</td>
              <td style={{ padding: '10px 8px', color: row.color }}>{row.imp}</td>
              <td style={{ padding: '10px 8px' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '2px 10px',
                  borderRadius: '6px',
                  background: `${row.color}20`,
                  color: row.color,
                  fontWeight: 700,
                  fontSize: '13px',
                }}>{row.rank}</span>
              </td>
              <td style={{ padding: '10px 8px', color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>{row.action}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const TasksPage = () => {
  const { t } = useLang();
  const [previewTask, setPreviewTask] = useState(null);
  const [headerRef, headerInView] = useInView();

  const tasks = [
    {
      id: 1,
      title: t('task1_title'),
      desc: t('task1_desc'),
      color: '#2E5AFF',
      tag: 'Задание 1',
      docContent: <PassportDoc />,
      icon: '💡',
    },
    {
      id: 2,
      title: t('task2_title'),
      desc: t('task2_desc'),
      color: '#5B8AFF',
      tag: 'Задание 2',
      docContent: <PassportDoc />,
      icon: '📋',
    },
    {
      id: 3,
      title: t('task3_title'),
      desc: t('task3_desc'),
      color: '#8A2BE2',
      tag: 'Задание 3',
      docContent: <WBSDoc />,
      icon: '📊',
    },
    {
      id: 4,
      title: t('task4_title'),
      desc: t('task4_desc'),
      color: '#C050FF',
      tag: 'Задание 4',
      docContent: <RiskDoc />,
      icon: '⚠️',
    },
    {
      id: 5,
      title: t('task5_title'),
      desc: t('task5_desc'),
      color: '#FF3B9A',
      tag: 'Задание 5',
      docContent: <PassportDoc />,
      icon: '🎯',
    },
  ];

  return (
    <div style={{ minHeight: '100vh', paddingTop: '80px' }}>
      {/* Header */}
      <div
        ref={headerRef}
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '60px 24px 48px',
          opacity: headerInView ? 1 : 0,
          transform: headerInView ? 'translateY(0)' : 'translateY(40px)',
          transition: 'all 0.8s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ width: '40px', height: '2px', background: 'linear-gradient(90deg, #2E5AFF, #FF3B9A)' }} />
          <span style={{ fontSize: '12px', fontWeight: 700, color: '#2E5AFF', letterSpacing: '3px', textTransform: 'uppercase' }}>
            Assignments
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px', alignItems: 'end' }}>
          <h1 style={{
            fontSize: 'clamp(28px, 4vw, 48px)',
            fontWeight: 900,
            letterSpacing: '-1.5px',
            lineHeight: 1.1,
            color: '#fff',
          }}>
            Практические{' '}
            <span style={{
              background: 'linear-gradient(135deg, #8A2BE2, #FF3B9A)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>задания</span>
          </h1>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 }}>
            {t('tasks_sub')}
          </p>
        </div>
      </div>

      {/* Tasks grid */}
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '0 24px 80px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
        gap: '20px',
      }}>
        {tasks.map((task, i) => (
          <TaskCard key={task.id} task={task} index={i} onPreview={() => setPreviewTask(task)} t={t} />
        ))}
      </div>

      {/* Preview modal */}
      {previewTask && (
        <DocumentPreview task={previewTask} onClose={() => setPreviewTask(null)} />
      )}

      <CTABanner />
    </div>
  );
};

const TaskCard = ({ task, index, onPreview, t }) => {
  const [ref, inView] = useInView();
  const [hovered, setHovered] = useState(false);

  return (
    <div
      ref={ref}
      style={{
        background: 'rgba(18,22,36,0.7)',
        backdropFilter: 'blur(20px)',
        border: `1px solid ${hovered ? task.color + '50' : 'rgba(255,255,255,0.06)'}`,
        borderRadius: '20px',
        padding: '28px',
        transition: 'all 0.3s ease',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transitionProperty: 'all',
        transitionDuration: '0.6s',
        transitionDelay: `${index * 0.1}s`,
        borderTop: `3px solid ${task.color}`,
        boxShadow: hovered ? `0 20px 60px ${task.color}20` : 'none',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '14px',
          background: `${task.color}15`,
          border: `1px solid ${task.color}30`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          flexShrink: 0,
          transform: hovered ? 'scale(1.1) rotate(5deg)' : 'scale(1)',
          transition: 'transform 0.3s ease',
        }}>
          {task.icon}
        </div>
        <div>
          <div style={{
            fontSize: '11px',
            fontWeight: 700,
            color: task.color,
            letterSpacing: '1px',
            marginBottom: '6px',
          }}>
            {task.tag}
          </div>
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#fff', lineHeight: 1.4 }}>
            {task.title}
          </h3>
        </div>
      </div>

      <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, marginBottom: '24px' }}>
        {task.desc}
      </p>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={onPreview}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '11px 16px',
            background: `${task.color}18`,
            border: `1px solid ${task.color}35`,
            borderRadius: '10px',
            color: task.color,
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={e => e.currentTarget.style.background = `${task.color}28`}
          onMouseOut={e => e.currentTarget.style.background = `${task.color}18`}
        >
          <Eye size={14} />
          {t('tasks_view_example')}
        </button>
        <button
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '11px 16px',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '10px',
            color: 'rgba(255,255,255,0.5)',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
          onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
        >
          <Download size={14} />
          Шаблон
        </button>
      </div>
    </div>
  );
};

export default TasksPage;
